import React, { useContext, useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { cloneDeep } from 'lodash';
import { IconButton } from '@material-ui/core';
import ControlPointIcon from '@material-ui/icons/ControlPoint';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';

import { CheckboxField as Checkbox } from 'components/checkbox-field/checkbox-field';
import { TooltipWrapper } from 'components/tooltip/tooltip-wrapper';
import { TextField } from 'components/text-field/text-field';

import { StructMapItemProps } from './struct-map-types';
import { StructMapCtx } from './struct-map-ctx';
import {
  getChildrenLeavesIds,
  addNode,
  removeNodes,
  moveNode,
  editLabel,
  getAllFiles,
} from './struct-map-utils';
import classNames from 'classnames';

export function StructMapItemNode({ label, data }: StructMapItemProps) {
  const {
    allowDrag,
    allowDrop,
    allowAdd,
    allowEdit,
    allowDelete,
    allowSelect,
    allowSpecialSelect,
    allData,
    onChange,
    classes,
    nodeTooltipMapper,
    NodeActions,
    selectedNodes,
    setSelectedNodes,
    specialSelectedNodes,
    setSpecialSelectedNodes,
    draggedNodes,
    setDraggedNodes,
    editingNodes,
    setEditingNodes,
    missingNodes,
    extraNodes,
  } = useContext(StructMapCtx);

  /**
   * All ids of leaves of current node.
   */
  const children = getChildrenLeavesIds(data, data.id!);

  const isRoot = allData.id === data.id;
  const isEditing = editingNodes.some((en) => en === data.id);

  const [editingLabel, setEditingLabel] = useState<string>(data.label!);

  /**
   * TRUE if all leaves of current node are selected.
   */
  const found =
    children.length && children.every((child) => selectedNodes.includes(child));

  /**
   * TRUE if all leaves of current node are special selected.
   */
  const specialFound =
    children.length &&
    children.every((child) => specialSelectedNodes.includes(child));

  /**
   * TRUE if at least one of leaves of current node is selected.
   *
   * Used to specify "indeterminate" prop.
   */
  const partiallyFound = children.some((child) =>
    selectedNodes.includes(child)
  );

  /**
   * TRUE if at least one of leaves of current node is special selected.
   *
   * Used to specify "indeterminate" prop.
   */
  const specialPartiallyFound = children.some((child) =>
    specialSelectedNodes.includes(child)
  );

  /**
   * Flag that indicates missing item in new version of struct map.
   */
  const isMissing = missingNodes.some((missingId) => missingId === data.id);

  /**
   * Flag that indicates extra item in new version of struct map.
   */
  const isExtra = extraNodes.some((extraId) => extraId === data.id);

  /**
   * Toggle select children leaves.
   */
  const toggleSelected = useCallback(() => {
    if (!!found) {
      setSelectedNodes((nodes) => [
        ...nodes.filter((node) => !children.includes(node)),
      ]);
    } else {
      setSelectedNodes((nodes) => [...nodes, ...children]);
    }
  }, [children, found, setSelectedNodes]);

  /**
   * Toggle special select children leaves.
   */
  const toggleSpecialSelected = useCallback(() => {
    if (!!specialFound) {
      setSpecialSelectedNodes((nodes) => [
        ...nodes.filter((node) => !children.includes(node)),
      ]);
    } else {
      setSpecialSelectedNodes((nodes) => [...nodes, ...children]);
    }
  }, [children, setSpecialSelectedNodes, specialFound]);

  const tooltip =
    !draggedNodes.length && nodeTooltipMapper
      ? nodeTooltipMapper(data)
      : undefined;

  /**
   *
   * @param event
   */
  function handleAllowDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
  }

  /**
   *
   */
  const handleDragStart = useCallback(() => {
    if (selectedNodes.length !== 0) {
      const draggedFiles = getAllFiles(allData).filter((file) =>
        selectedNodes.some((sid) => sid === file.id)
      );

      setDraggedNodes(draggedFiles);
    } else {
      setDraggedNodes([data]);
    }
  }, [allData, data, selectedNodes, setDraggedNodes]);

  /**
   *
   */
  const handleDrop = useCallback(() => {
    if (allowDrop && draggedNodes.length && onChange) {
      const clonedData = cloneDeep(allData);

      draggedNodes.forEach((dn) => moveNode(clonedData, dn, data.id!));
      onChange(clonedData);

      setDraggedNodes([]);
    } else {
      setDraggedNodes([]);
    }
  }, [allData, allowDrop, data.id, draggedNodes, onChange, setDraggedNodes]);

  /**
   *
   */
  const handleAdd = useCallback(() => {
    const newData = addNode(
      cloneDeep(allData),
      { id: uuidv4(), label: '' },
      data.id!
    );

    onChange(newData);
  }, [allData, data.id, onChange]);

  /**
   *
   */
  const handleEdit = useCallback(() => {
    if (isEditing) {
      setEditingNodes(editingNodes.filter((e) => e !== data.id));
    } else {
      setEditingNodes([...editingNodes, data.id!]);
    }
  }, [data.id, editingNodes, isEditing, setEditingNodes]);

  /**
   *
   */
  const handleEditLabel = useCallback(() => {
    const newData = editLabel(cloneDeep(allData), editingLabel, data.id!);

    onChange(newData);
    handleEdit();
  }, [allData, data.id, editingLabel, handleEdit, onChange]);

  /**
   *
   */
  const handleDelete = useCallback(() => {
    const newData = removeNodes(cloneDeep(allData), [data.id!]);

    onChange(newData);
  }, [allData, data.id, onChange]);

  return (
    <div
      className={classes.item}
      draggable={allowDrag}
      onDragStart={handleDragStart}
      onDragOver={(e) => handleAllowDrop(e)}
      onDrop={handleDrop}
    >
      <TooltipWrapper tooltip={tooltip}>
        {isEditing ? (
          <div style={{ flex: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <TextField
              name={data.id!}
              value={editingLabel}
              disabled={false}
              onChange={(value) => setEditingLabel(value.toString())}
            />
          </div>
        ) : (
          <div
            className={classNames(classes.label, {
              [classes.missingItem]: isMissing,
              [classes.extraItem]: isExtra,
            })}
          >
            {label}
          </div>
        )}
      </TooltipWrapper>
      <div onClick={(e) => e.stopPropagation()} className={classes.actions}>
        <NodeActions node={data} />
        {allowEdit &&
          (isEditing ? (
            <>
              <IconButton
                classes={{ root: classes.crudButton }}
                onClick={handleEditLabel}
              >
                <CheckIcon className={classes.crudIcon} />
              </IconButton>
              <IconButton
                classes={{ root: classes.crudButton }}
                onClick={handleEdit}
              >
                <ClearIcon className={classes.crudIcon} />
              </IconButton>
            </>
          ) : (
            <IconButton
              classes={{ root: classes.crudButton }}
              onClick={handleEdit}
            >
              <EditIcon className={classes.crudIcon} />
            </IconButton>
          ))}

        {allowAdd && (
          <IconButton
            classes={{ root: classes.crudButton }}
            onClick={handleAdd}
          >
            <ControlPointIcon className={classes.crudIcon} />
          </IconButton>
        )}

        {allowDelete && (
          <IconButton
            classes={{ root: classes.crudButton }}
            onClick={handleDelete}
            disabled={isRoot}
          >
            <DeleteIcon className={classes.crudIcon} />
          </IconButton>
        )}

        {allowSelect && (
          <Checkbox
            name="found"
            value={!!found}
            onChange={toggleSelected}
            disabled={!children.length}
            indeterminate={!!partiallyFound && !found}
          />
        )}
        {allowSpecialSelect && (
          <Checkbox
            name="specialFound"
            value={!!specialFound}
            onChange={toggleSpecialSelected}
            disabled={!children.length}
            indeterminate={!!specialPartiallyFound && !found}
          />
        )}
      </div>
    </div>
  );
}
